import React, { useEffect, useRef, useState } from "react";
import { map, mergeRight, prop, sortBy } from "ramda";

import PanelLoadingIndicator from "components/PanelLoadingIndicator";
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { fetchSecurityUser } from "util/fetchUtils";
import { logOut } from "auth/authActions";
import AddIcon from "@material-ui/icons/Add";
import { Container } from "@material-ui/core";
import { notBlank } from "../../../util/ext";
import { useSftpAccounts, useSftpAccountUpdate } from "../sftpAccountServices";
import SftpAccountListItem from "./SftpAccountListItem";
import EditSftpAccount from "./EditSftpAccount";
import SftpAccountPasswordUpdate from "./SftpAccountPasswordUpdate";

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    panel: {
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.primary.light,
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },

    toolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      height: 60,
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
      padding: theme.spacing(4),
    },

    leftContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      minWidth: 200,
    },

    rightContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      flexGrow: 1,
    },

    title: {
      flexGrow: 1,
      display: "flex",
      alignItems: "center",
      fontSize: "2.0rem",
      fontWeight: "bold",
    },

    newButton: {
      textTransform: "uppercase",
      color: "#127DA9",
      fontSize: "1.2rem",
      fontWeight: "bold",
    },

    listContainer: {
      height: "calc(100vh - 275px)",
      overflow: "auto",
    },
  }));

const userCache = new Map();
const lookupUser = (auth, userId) => {
  if (!userCache.has(userId)) {
    userCache.set(userId, fetchSecurityUser(auth)(userId));
  }

  return userCache.get(userId);
};

const SftpAccountBrowse = () => {
  const [scrollVisible, setScrollVisible] = useState(true);

  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();

  // const userCacheRef = useRef(new Map());
  const [accounts, setAccounts] = useState([]);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [passwordDialogVisible, setPasswordDialogVisible] = useState(false);
  const [currentAccount, setCurrentAccount] = useState({});
  const classes = useStyles(scrollVisible)();

  const results = useSftpAccounts();
  useEffect(() => {
    if (results.isError && results.error.status === 401) {
      dispatch(logOut());
    }

    const fetchUsers = async acct => {
      const createdByUser = await lookupUser(auth, acct.createdBy);
      const updatedByUser = await lookupUser(auth, acct.updatedBy);

      return mergeRight(acct, {
        createdByUsername: createdByUser.username,
        updatedByUsername: updatedByUser.username,
      });
    };

    if (results.isSuccess && notBlank(results.data)) {
      setScrollVisible(true);
      Promise.all(map(fetchUsers, results.data)).then(data => {
        setAccounts(sortBy(prop("accountName"), data));
      });
    }
  }, [
    dispatch,
    auth,
    results.data,
    results.isError,
    results.error,
    results.isSuccess,
  ]);

  const handleDialogOnCancel = () => {
    setEditDialogVisible(false);
    setPasswordDialogVisible(false);
  };

  const handleDialogOnSave = async account => {
    setCurrentAccount(account);
    await results.refetch();
    setEditDialogVisible(false);
    setPasswordDialogVisible(false);
  };

  const handleAddAccountOnClick = () => {
    setCurrentAccount({});
    setEditDialogVisible(true);
  };

  const handleEditAccountOnClick = account => {
    setCurrentAccount(account);
    setEditDialogVisible(true);
  };

  const handlePasswordOnClick = account => {
    setCurrentAccount(account);
    setPasswordDialogVisible(true);
  };

  const accountUpdate = useSftpAccountUpdate();
  const handleDeactivateAccountOnClick = async account => {
    setCurrentAccount(account);
    const mergedAccount = mergeRight(account, {
      active: false,
    });

    await accountUpdate.mutateAsync({
      resourceIds: { sftpAccountId: mergedAccount.sftpAccountId },
      data: mergedAccount,
    });
    await results.refetch();
  };

  const handleActivateAccountOnClick = async account => {
    setCurrentAccount(account);
    const mergedAccount = mergeRight(account, {
      active: true,
    });

    await accountUpdate.mutateAsync({
      resourceIds: { sftpAccountId: mergedAccount.sftpAccountId },
      data: mergedAccount,
    });
    await results.refetch();
  };

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl) && notBlank(tableContainerEl.current)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      console.log(`Scroll Visible : ${scrollVisible}`);
      setScrollVisible(scrollVisible);
    }
  }, [tableContainerEl]);

  return (
    <Container id="sftp-account-browse-view" classes={{ root: classes.panel }}>
      <EditSftpAccount
        open={editDialogVisible}
        account={currentAccount}
        onSave={handleDialogOnSave}
        onClose={handleDialogOnCancel}
      />

      <SftpAccountPasswordUpdate
        open={passwordDialogVisible}
        account={currentAccount}
        onSave={handleDialogOnSave}
        onClose={handleDialogOnCancel}
      />

      <div>
        <div>
          <div className={classes.toolbar}>
            <div className={classes.leftContainer}>
              <div className={classes.title}>Accounts</div>
            </div>

            <div>
              <div>
                <Button
                  variant={"text"}
                  startIcon={<AddIcon />}
                  classes={{ root: classes.newButton }}
                  onClick={() => handleAddAccountOnClick(true)}
                >
                  New Account
                </Button>
              </div>
            </div>
          </div>
        </div>

        {results.isLoading && <PanelLoadingIndicator />}
        {results.isSuccess && notBlank(accounts) && (
          <Container classes={{ root: classes.panel }}>
            <Container ref={tableContainerEl} className={classes.listContainer}>
              <List>
                {map(account => {
                  const selected =
                    account.sftpAccountId === currentAccount.sftpAccountId ||
                    account.accountName === currentAccount.accountName;
                  return (
                    <SftpAccountListItem
                      key={account.sftpAccountId}
                      account={account}
                      selected={selected}
                      userLookup={lookupUser}
                      onEditClick={handleEditAccountOnClick}
                      onDeactivateClick={handleDeactivateAccountOnClick}
                      onActivateClick={handleActivateAccountOnClick}
                      onPasswordClick={handlePasswordOnClick}
                    />
                  );
                }, accounts)}
              </List>
            </Container>
          </Container>
        )}
        {results.isError && <div>Error</div>}
      </div>
    </Container>
  );
};

export default SftpAccountBrowse;
